/*---------------------------------------------------------------------------------------------
 *  Copyright (c) 2023 Vienna Hypertext Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/

import { useRef, useState } from "react";

import styled from "styled-components";
import { useOnClickOutside } from "usehooks-ts";

import { Layer } from "@vienna/common";

import { ShortcutsMenu } from "src/components/Plate/HelpMenu/ShortcutsMenu";
import useIsomorphicLayoutEffect from "src/lib/hooks/useIsomorphicLayoutEffect";

export const HelpMenu = () => {
  const helpMenuRef = useRef<HTMLDivElement>(null);
  const [menuVisible, setMenuVisible] = useState<boolean>(false);
  const [shortcutsVisible, setShortcutsVisible] = useState<boolean>(false);
  const [fixedTop, setFixedTop] = useState<number>(0);
  const close = () => {
    setMenuVisible(false);
    setShortcutsVisible(false);
  };

  useIsomorphicLayoutEffect(() => {
    setFixedTop((helpMenuRef.current?.parentElement as HTMLElement).getBoundingClientRect().top);
  });

  useOnClickOutside(helpMenuRef, close);

  return (
    <HelpWrapper ref={helpMenuRef} id="karls-specialty-menu">
      {shortcutsVisible && <ShortcutsMenu fixedTop={fixedTop} close={close} />}
      <HelpButtonWrapper
        visible={menuVisible || shortcutsVisible}
        onClick={() => {
          setShortcutsVisible(!shortcutsVisible);
        }}
      >
        ?
      </HelpButtonWrapper>
    </HelpWrapper>
  );
};

const HelpWrapper = styled.div`
  position: absolute;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  bottom: 20px;
  right: 40px;
  z-index: ${Layer.WIN_LAYER};
  @media print {
    display: none;
  }
`;

const HelpButtonWrapper = styled.div<{ visible: boolean }>`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 40px;
  width: 40px;
  border: ${({ theme }) => `1px solid ${theme.primary}`};
  color: ${({ theme }) => theme.primary};
  border-radius: 20px;
  background: ${({ theme }) => theme.background};
  font-size: 30px;
  font-family: "IBM Plex Mono";
  font-weight: 300;
  cursor: pointer;
  opacity: ${({ visible }) => (visible ? 1 : 0.2)};
  transition: opacity 0.3s ease;
  user-select: none;
  &:hover {
    opacity: 1;
  }
`;
